import { setType } from '@/common/utils/typedValueSerializer'
import UniDataTableRow from './UniDataTableRow'

interface TableData {
  rows?: any[]
  attributes?: Record<string, any>
  columns?: any[]
}

/**
 * UniDataTable
 */
export default class UniDataTable {
  attributes: Record<string, any>
  columns: string[]
  rows: UniDataTableRow[]
  empty: boolean

  constructor(data: TableData | any[] = {}, rowsMaybe?: any, attrsMaybe?: any) {
    this.attributes = {}
    this.columns = []
    this.rows = []

    if (
      Array.isArray(data) &&
      (data.length === 0 || data.every((x) => typeof x === 'string')) &&
      rowsMaybe !== undefined &&
      rowsMaybe !== null
    ) {
      this.columns = [...(data as string[])]
      this.attributes =
        attrsMaybe != null && typeof attrsMaybe === 'object' && !Array.isArray(attrsMaybe)
          ? { ...(attrsMaybe as Record<string, unknown>) }
          : {}
      const rawRows = Array.isArray(rowsMaybe) ? rowsMaybe : []
      this.rows = rawRows.map((row, idx) => new UniDataTableRow(row, idx, this.setField.bind(this)))
      this.empty = this.rows.length === 0
      return
    }

    if (Array.isArray(data)) {
      // new UniDataTable(String[] columns) — массив строк = имена колонок, строк нет
      const isColumnNames = data.length === 0 || data.every((x) => typeof x === 'string')
      if (isColumnNames) {
        this.columns = [...data]
        this.rows = []
      } else {
        this.rows = data.map((row, idx) => new UniDataTableRow(row, idx, this.setField.bind(this)))
      }
    } else {
      // Объект { columns?, rows?, attributes? }
      this.columns = data.columns ?? []
      this.attributes = data.attributes ?? {}
      if (Array.isArray(data.rows)) {
        this.rows = data.rows.map((row, idx) => new UniDataTableRow(row, idx, this.setField.bind(this)))
      } else {
        this.rows = []
      }
    }

    this.empty = this.rows.length === 0
  }

  /**
   * Проверяет является ли таблица пустой
   */
  isEmpty(): boolean {
    return this.rows.length === 0
  }

  /**
   * Создаёт новую строку и добавляет в таблицу
   */
  newRow(data: any = {}): UniDataTableRow {
    const row = new UniDataTableRow(data, this.rows.length, this.setField.bind(this))
    this.rows.push(row)
    this.empty = false
    return row
  }

  /**
   * Добавляет строку в конец или вставляет по индексу (fatclient: addRow(row) / addRow(0, row)).
   */
  addRow(rowOrIndex: UniDataTableRow | any, maybeRow?: UniDataTableRow | any): UniDataTableRow {
    if (maybeRow !== undefined) {
      const index = Math.trunc(Number(rowOrIndex))
      const clamped = Math.max(0, Math.min(index, this.rows.length))
      const inserted = new UniDataTableRow(maybeRow, clamped, this.setField.bind(this))
      this.rows.splice(clamped, 0, inserted)
      this.rows.forEach((r, i) => {
        if (r instanceof UniDataTableRow) r.idx = i
      })
      this.empty = this.rows.length === 0
      return inserted
    }
    const row =
      rowOrIndex instanceof UniDataTableRow
        ? rowOrIndex
        : new UniDataTableRow(rowOrIndex ?? {}, this.rows.length, this.setField.bind(this))
    this.rows.push(row)
    this.empty = false
    return row
  }

  /**
   * Добавляет все строки другой таблицы в конец (совместимость с fat-клиентом: UniDataTable.add).
   */
  add(other: UniDataTable | any): this {
    if (other == null) return this
    const sourceRows: UniDataTableRow[] | null =
      other instanceof UniDataTable
        ? other.rows
        : Array.isArray((other as { rows?: unknown })?.rows)
          ? ((other as { rows: UniDataTableRow[] }).rows as UniDataTableRow[])
          : null
    if (!sourceRows?.length) return this
    for (const r of sourceRows) {
      const row = r instanceof UniDataTableRow ? r : new UniDataTableRow(r as any, 0, () => {})
      this.addRow(row.clone())
    }
    return this
  }

  /**
   * Доступ к строке по индексу
   */
  get(index: number): UniDataTableRow {
    return this.getRow(index)
  }

  /**
   * Удаляет строку по индексу
   */
  removeRow(idx: number): void {
    this.rows.splice(idx, 1)
    this.rows.forEach((r, i) => {
      if (r instanceof UniDataTableRow) r.idx = i
    })
    if (!this.rows.length) this.empty = true
  }

  /**
   * Все строки
   */
  getRows(): UniDataTableRow[] {
    return this.rows.map((row, idx) => new UniDataTableRow(row, idx, this.setField.bind(this)))
  }

  /**
   * Массив строк
   */
  toArray(): UniDataTableRow[] {
    return this.getRows()
  }

  /**
   * Очистка таблицы
   */
  clear(): void {
    this.rows = []
    this.empty = true
  }

  /**
   * Получает строку по индексу
   */
  getRow(idx: number): UniDataTableRow {
    const row = this.rows[idx]
    if (row == null) {
      return new UniDataTableRow({}, idx, () => {})
    }
    return new UniDataTableRow(row, idx, this.setField.bind(this))
  }

  /**
   * Возвращает первую строку из таблицы, в которой поле fieldname равняется value
   * (Swing: сравнение через getFieldAsString; Boolean → "1"/"0")
   */
  findFirstRow(fieldname: string, value: any): UniDataTableRow | null {
    const expect = String(value)
    for (let i = 0; i < this.rows.length; i++) {
      const wrapped = new UniDataTableRow(this.rows[i], i, this.setField.bind(this))
      if (wrapped.getFieldAsString(fieldname) === expect) return wrapped
    }
    return null
  }

  /**
   * Удаляет из таблицы строки, в которых поле fieldname равняется value
   * (Swing: сравнение через getFieldAsString; Boolean → "1"/"0")
   */
  removeRowsConteinsValue(fieldname: string, value: any): this {
    const expect = String(value)
    this.rows = this.rows.filter((row, i) => {
      const wrapped = new UniDataTableRow(row, i, this.setField.bind(this))
      return wrapped.getFieldAsString(fieldname) !== expect
    })
    return this
  }

  /**
   * Устанавливает значение данного поля строки под индексом rowIdx
   */
  setField(fieldName: string, value: any, rowIdx: number): void {
    const row = this.rows[rowIdx]
    if (row == null || typeof row !== 'object') return
    const typed = setType(value)
    if (row instanceof UniDataTableRow) {
      row.fields[fieldName] = typed
      return
    }
    if (!row.fields || typeof row.fields !== 'object' || Array.isArray(row.fields)) {
      row.fields = {}
    }
    row.fields[fieldName] = typed
  }

  /**
   * Количество строк
   */
  size(): number {
    return this.rows?.length || 0
  }

  /**
   * Получает массив колонок
   */
  getColumns(): any[] {
    return this.columns
  }
}
